if (!customElements.get('sleep-quiz')) {
  customElements.define('sleep-quiz', class SleepQuiz extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.form = this.querySelector('.sleep-quiz__form');
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.results = this.querySelector('[data-quiz-results]');
      this.nextButton = this.querySelector('[data-quiz-next]');
      this.backButton = this.querySelector('[data-quiz-back]');
      this.restartButton = this.querySelector('[data-quiz-restart]');
      this.error = this.querySelector('[data-quiz-error]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.progressLabel = this.querySelector('[data-progress-label]');
      this.downloadSummaryButton = this.querySelector('[data-quiz-download-summary]');
      this.shareSummaryButton = this.querySelector('[data-quiz-share-summary]');
      this.shareToast = this.querySelector('[data-share-toast]');
      this.summaryFirmness = this.querySelector('[data-summary-firmness]');
      this.summaryClassification = this.querySelector('[data-summary-classification]');
      this.summaryType = this.querySelector('[data-summary-type]');
      this.summaryFirmnessBar = this.querySelector('[data-summary-firmness-bar]');
      this.resultBudget = this.querySelector('[data-result-budget]');
      this.catalog = this.querySelector('[data-quiz-catalog]');
      this.currentStep = 0;
      this.quizId = 'mattress_match';
      this.quizVersion = '8_question_budget_v1';
      this.questionIds = ['position', 'weight', 'age', 'motion', 'pain', 'frequency', 'temperature', 'budget'];
      this.attemptNumber = 1;
      this.quizStarted = false;
      this.answeredSteps = new Set();
      this.completionTrackedForAttempt = false;

      if (!this.form || this.steps.length === 0) return;

      this.nextButton.addEventListener('click', () => this.advance());
      this.backButton.addEventListener('click', () => this.goBack());
      this.restartButton.addEventListener('click', () => this.restart());
      if (this.downloadSummaryButton) {
        this.downloadSummaryButton.addEventListener('click', () => this.downloadSleepMatch());
      }
      if (this.shareSummaryButton) {
        this.shareSummaryButton.addEventListener('click', () => this.shareSleepMatch());
      }
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.advance();
      });
      this.form.addEventListener('change', (event) => this.handleChange(event));
      this.addEventListener('click', (event) => this.handleTrackedClick(event));

      this.catalogPromise = this.loadCatalogPages();
      this.showStep(0);
    }

    async loadCatalogPages() {
      if (!this.catalog) return;

      const destination = this.catalog.querySelector('[data-quiz-catalog-page]') || this.catalog;
      const productIds = new Set(
        Array.from(this.catalog.querySelectorAll('[data-quiz-product]'))
          .map((product) => product.dataset.productId)
          .filter(Boolean)
      );
      let nextUrl = destination.dataset.nextUrl || '';
      let pageCount = 1;

      while (nextUrl && pageCount < 10) {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) break;

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const nextCatalog = documentFragment.querySelector('[data-quiz-catalog]');
        const nextPage = nextCatalog?.querySelector('[data-quiz-catalog-page]');
        if (!nextPage) break;

        Array.from(nextPage.querySelectorAll('[data-quiz-product]')).forEach((product) => {
          const productId = product.dataset.productId;
          if (!productId || productIds.has(productId)) return;
          productIds.add(productId);
          destination.appendChild(product);
        });

        nextUrl = nextPage.dataset.nextUrl || '';
        pageCount += 1;
      }

      this.catalog.dataset.loadedProducts = String(productIds.size);
    }

    trackEvent(eventName, parameters = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        quiz_id: this.quizId,
        quiz_version: this.quizVersion,
        attempt_number: this.attemptNumber,
        ...parameters
      });
    }

    startQuizTracking() {
      if (this.quizStarted) return;
      this.quizStarted = true;
      this.trackEvent('sleep_quiz_start', {
        question_count: this.steps.length
      });
    }

    trackCurrentQuestionAnswered() {
      if (this.answeredSteps.has(this.currentStep)) return;
      this.answeredSteps.add(this.currentStep);
      this.trackEvent('sleep_quiz_question_answered', {
        question_number: this.currentStep + 1,
        question_id: this.questionIds[this.currentStep] || `question_${this.currentStep + 1}`,
        question_count: this.steps.length
      });
    }

    resultAnalyticsParameters() {
      const prescription = this.latestPrescription;
      if (!prescription) return {};
      const budgetBand = this.latestAnswers?.budget || 'flexible';
      const parameters = {
        firmness_score: Number(prescription.firmness.toFixed(1)),
        firmness_label: prescription.classification,
        construction: prescription.type,
        budget_band: budgetBand,
        recommendation_count: (this.latestMatches || []).length
      };
      const budgetEstimate = this.budgetAnalyticsEstimate(budgetBand);
      if (Number.isFinite(budgetEstimate)) parameters.budget_estimate_cad = budgetEstimate;
      return parameters;
    }

    handleTrackedClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const showroomLink = target.closest('[data-showroom-location]');
      if (showroomLink && this.contains(showroomLink)) {
        this.trackEvent('sleep_quiz_showroom_cta_clicked', {
          showroom_location: showroomLink.dataset.showroomLocation,
          cta_context: 'quiz_results'
        });
        return;
      }

      const recommendationLink = target.closest('[data-result-products] a');
      if (!recommendationLink || !this.contains(recommendationLink)) return;
      const card = recommendationLink.closest('[data-quiz-product]');
      if (!card) return;

      let clickTarget = 'button';
      if (recommendationLink.matches('.sleep-quiz__product-media')) clickTarget = 'image';
      if (recommendationLink.closest('h3')) clickTarget = 'title';

      const productName = card.querySelector('h3')?.textContent.trim() || 'Mattress recommendation';
      this.trackEvent('sleep_quiz_recommendation_clicked', {
        product_id: card.dataset.productId || '',
        product_name: productName,
        recommendation_rank: Number(card.dataset.recommendationRank || 0),
        match_score: Number(card.dataset.matchScore || 0),
        click_target: clickTarget
      });
    }

    handleChange(event) {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.form === this.form) {
        this.startQuizTracking();
      }
      
      if (input.name === 'pain' && input.checked) {
        const painInputs = Array.from(this.form.querySelectorAll('input[name="pain"]'));
        if (input.hasAttribute('data-exclusive')) {
          painInputs.forEach((item) => {
            if (item !== input) item.checked = false;
          });
        } else {
          const exclusive = painInputs.find((item) => item.hasAttribute('data-exclusive'));
          if (exclusive) exclusive.checked = false;
        }
      }

      this.updateSelectionClasses();
      this.hideError();
    }

    advance() {
      if (!this.isCurrentStepValid()) {
        this.error.hidden = false;
        const firstInput = this.steps[this.currentStep].querySelector('input');
        if (firstInput) firstInput.focus({ preventScroll: true });
        return;
      }

      this.hideError();
      this.trackCurrentQuestionAnswered();
      if (this.currentStep < this.steps.length - 1) {
        this.showStep(this.currentStep + 1);
      } else {
        this.complete();
      }
    }

    goBack() {
      if (this.currentStep > 0) this.showStep(this.currentStep - 1);
    }

    showStep(index) {
      this.currentStep = index;
      this.steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== index;
      });

      const progress = ((index + 1) / this.steps.length) * 100;
      this.progressBar.style.width = `${progress}%`;
      this.progressLabel.textContent = `Question ${index + 1} of ${this.steps.length}`;
      this.backButton.hidden = index === 0;
      this.nextButton.textContent = index === this.steps.length - 1 ? 'See my matches' : 'Next question';
      this.hideError();

      if (index > 0) {
        const legend = this.steps[index].querySelector('legend');
        if (legend) {
          legend.setAttribute('tabindex', '-1');
          legend.focus({ preventScroll: true });
        }
      }
      this.updateSelectionClasses();
    }

    isCurrentStepValid() {
      return Boolean(this.steps[this.currentStep].querySelector('input:checked'));
    }

    hideError() {
      this.error.hidden = true;
    }

    async complete() {
      const originalButtonText = this.nextButton.textContent;
      this.nextButton.disabled = true;
      this.nextButton.textContent = 'Finding your matches…';
      try {
        await this.catalogPromise;
      } catch (error) {
        console.warn('The complete mattress catalog could not be loaded.', error);
      }

      const answers = this.getAnswers();
      const prescription = this.buildPrescription(answers);

      this.latestAnswers = answers;
      this.latestPrescription = prescription;

      this.renderPrescription(prescription);
      this.renderMatches(answers, prescription);
      this.form.hidden = true;
      this.results.hidden = false;
      this.nextButton.disabled = false;
      this.nextButton.textContent = originalButtonText;

      const resultHeading = this.results.querySelector('h2');
      if (resultHeading) {
        resultHeading.setAttribute('tabindex', '-1');
        resultHeading.focus({ preventScroll: true });
      }
      this.results.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });

      const analyticsDetail = {
        firmness: prescription.firmness,
        classification: prescription.classification,
        construction: prescription.type,
        budget: answers.budget
      };
      this.dispatchEvent(new CustomEvent('sleep-quiz:completed', { bubbles: true, detail: analyticsDetail }));
      if (!this.completionTrackedForAttempt) {
        this.trackEvent('sleep_quiz_completed', {
          question_count: this.steps.length,
          ...this.resultAnalyticsParameters()
        });
        this.completionTrackedForAttempt = true;
      }
    }

    getAnswers() {
      const data = new FormData(this.form);
      return {
        position: data.get('position'),
        weight: data.get('weight'),
        age: data.get('age'),
        motion: data.get('motion'),
        pain: data.getAll('pain'),
        frequency: data.get('frequency'),
        temperature: data.get('temperature'),
        budget: data.get('budget')
      };
    }

    buildPrescription(answers) {
      const profiles = {
        side: { firmness: 4.5, type: 'foam', reason: 'Side sleeping benefits from deeper shoulder and hip contouring.' },
        back: { firmness: 6.5, type: 'hybrid', reason: 'Back sleeping benefits from balanced lumbar support that limits sagging.' },
        stomach: { firmness: 8, type: 'innerspring', reason: 'Stomach sleeping needs a firmer surface to keep the hips from sinking too deeply.' },
        combination: { firmness: 6, type: 'hybrid', reason: 'Combination sleeping benefits from a responsive surface that makes movement easier.' }
      };
      const base = profiles[answers.position] || profiles.combination;
      let firmness = base.firmness;
      let type = base.type;
      const reasons = [base.reason];

      if (answers.weight === 'heavy') {
        firmness += 1.5;
        type = 'hybrid';
        reasons.push('Your weight profile adds stronger coil support and durability to the recommendation.');
      } else if (answers.weight === 'light') {
        firmness -= 1;
        reasons.push('A slightly softer target should contour more readily under a lighter body weight.');
      }

      if (answers.age === '50plus' && type === 'innerspring') {
        type = 'hybrid';
        reasons.push('A hybrid adds joint cushioning while retaining edge support and easier movement.');
      }

      if (answers.motion === 'sensitive' && type === 'innerspring') {
        type = 'hybrid';
        reasons.push('Pocketed coils and comfort foam improve motion isolation for a light sleeper.');
      } else if (answers.motion === 'sensitive') {
        reasons.push('Strong motion isolation is prioritized because movement can interrupt your sleep.');
      }

      const hasBackPain = answers.pain.includes('back');
      const hasShoulderPain = answers.pain.includes('shoulder');
      const hasHipPain = answers.pain.includes('hip');

      if (hasBackPain) {
        firmness = 6.5;
        if (type === 'foam' && answers.weight !== 'light') type = 'hybrid';
        reasons.push('Lower-back discomfort shifts the target toward medium-firm alignment support.');
      }

      if (hasShoulderPain) {
        firmness = Math.min(firmness, 5);
        reasons.push('Shoulder discomfort caps firmness at a more cushioning level for pressure relief.');
      }

      if (hasHipPain) {
        reasons.push('Pressure-relieving or zoned comfort layers are prioritized for your hips.');
      }

      if (answers.temperature === 'hot' && type === 'foam') {
        type = 'hybrid';
        reasons.push('A breathable coil core replaces all-foam construction to improve airflow.');
      } else if (answers.temperature === 'hot') {
        reasons.push('Cooling materials and open airflow are prioritized for your temperature profile.');
      }

      firmness = Math.max(1, Math.min(10, Math.round(firmness * 2) / 2));
      return {
        firmness,
        type,
        classification: this.classifyFirmness(firmness),
        reasons: [...new Set(reasons)].slice(0, 6),
        advisory: answers.frequency === 'daily'
          ? 'Because discomfort affects you daily, consider confirming this starting point with a healthcare professional and testing alignment support in person.'
          : ''
      };
    }

    classifyFirmness(value) {
      if (value <= 2) return 'Extra Soft';
      if (value <= 3.5) return 'Soft';
      if (value <= 4.5) return 'Medium-Soft';
      if (value <= 5.5) return 'Medium';
      if (value <= 6.5) return 'Medium-Firm';
      if (value <= 7.5) return 'Firm';
      if (value <= 8.5) return 'Extra Firm';
      return 'Ultra Firm';
    }

    renderPrescription(prescription) {
      this.querySelector('[data-result-classification]').textContent = prescription.classification;
      this.querySelector('[data-result-firmness]').textContent = prescription.firmness.toFixed(1);
      this.querySelector('[data-result-type]').textContent = this.displayType(prescription.type);
      this.querySelector('[data-result-summary]').textContent =
        `Based on your sleep position, body profile, comfort concerns, and temperature needs, start near ${prescription.firmness.toFixed(1)} out of 10 in a ${this.displayType(prescription.type).toLowerCase()} mattress.`;
      if (this.resultBudget) {
        this.resultBudget.textContent = this.budgetLabel(this.latestAnswers?.budget);
      }

      const typeCopy = {
        foam: 'Close contouring and strong motion isolation create a pressure-relieving sleep surface.',
        hybrid: 'Comfort foams over a pocketed coil core balance pressure relief, airflow, and support.',
        innerspring: 'A responsive coil support system provides a firmer, easy-to-move-on sleep surface.'
      };
      this.querySelector('[data-result-type-copy]').textContent = typeCopy[prescription.type];
      const firmnessPosition = ((prescription.firmness - 1) / 9) * 100;
      this.querySelector('[data-firmness-marker]').style.left = `${firmnessPosition}%`;
      this.querySelector('.sleep-quiz__firmness-scale').style.setProperty('--sq-firmness-width', `${firmnessPosition}%`);

      const reasonList = this.querySelector('[data-result-reasons]');
      reasonList.replaceChildren(...prescription.reasons.map((reason) => {
        const item = document.createElement('li');
        item.textContent = reason;
        return item;
      }));

       const advisory = this.querySelector('[data-result-advisory]');
      advisory.hidden = !prescription.advisory;
      advisory.textContent = prescription.advisory;

      if (this.summaryFirmness) this.summaryFirmness.textContent = prescription.firmness.toFixed(1);
      if (this.summaryClassification) this.summaryClassification.textContent = prescription.classification;
      if (this.summaryType) this.summaryType.textContent = this.displayType(prescription.type);
      if (this.summaryFirmnessBar) this.summaryFirmnessBar.style.width = `${prescription.firmness * 10}%`;
    }

    renderMatches(answers, prescription) {
      const productContainer = this.querySelector('[data-result-products]');
      const noProducts = this.querySelector('[data-no-products]');
      const budget = this.budgetRange(answers.budget);
      const scoredProducts = Array.from(this.querySelectorAll('[data-quiz-product]'))
        .map((element) => this.scoreProduct(element, answers, prescription));
      const eligibleProducts = budget
        ? scoredProducts.filter((product) => this.priceIsWithinBudget(product.queenPriceCents, budget))
        : scoredProducts;
      const products = eligibleProducts
        .sort((a, b) => b.rawScore - a.rawScore)
        .slice(0, 2);

      this.latestMatches = products;

      productContainer.replaceChildren();
      noProducts.hidden = products.length > 0;
      if (products.length === 0) {
        noProducts.textContent = `We do not currently have an available Queen mattress inside ${this.budgetLabel(answers.budget)}. Choose “Show me the best fit” or visit a showroom for more options.`;
      }

      products.forEach((match, index) => {
        const card = match.element.cloneNode(true);
        card.dataset.recommendationRank = String(index + 1);
        card.dataset.matchScore = String(match.displayScore);
        card.querySelector('[data-match-label]').textContent = index === 0 ? 'Top pick' : 'Runner-up';
        card.querySelector('[data-match-score]').textContent = `${match.displayScore}%`;
        const reasonsList = card.querySelector('[data-match-reasons]');
        reasonsList.replaceChildren(...match.reasons.slice(0, 3).map((reason) => {
          const item = document.createElement('li');
          item.textContent = reason;
          return item;
        }));
        productContainer.appendChild(card);
      });
    }

    scoreProduct(element, answers, prescription) {
      const parsedFirmness = this.parseFirmness(element.dataset.firmness);
      const hasFirmness = Number.isFinite(parsedFirmness);
      const firmness = hasFirmness ? parsedFirmness : 5.5;
      const productType = this.normalizeType(`${element.dataset.type || ''} ${element.dataset.features || ''}`);
      const features = (element.dataset.features || '').toLowerCase();
      const bestFor = (element.dataset.bestFor || '').toLowerCase();
      const hasPain = answers.pain.some((value) => value !== 'none');
      const pressureRelief = this.parseMetric(element.dataset.pressureRelief, 5);
      const cooling = this.parseMetric(element.dataset.cooling, 5);
      const motionIsolation = this.parseMetric(element.dataset.motionIsolation, 5);
      const priceCents = Number.parseInt(element.dataset.priceCents || '', 10);
      const queenPriceCents = Number.parseInt(element.dataset.queenPriceCents || '', 10);
      const reasons = [];
      const firmnessDifference = Math.abs(firmness - prescription.firmness);
      const firmnessScore = hasFirmness ? Math.max(0, 42 - (firmnessDifference * 7)) : 10;
      let score = firmnessScore;

      if (productType === prescription.type) {
        score += 16;
        reasons.push(`${this.displayType(productType)} construction matches your recommended starting point`);
      } else if (productType === 'unknown') {
        score += 6;
      } else {
        score += 2;
      }

      if (hasFirmness && firmnessDifference <= 1) {
        reasons.push(`${this.classifyFirmness(firmness)} feel is close to your ${prescription.firmness.toFixed(1)} target`);
      }

      const zoneSupport = this.hasFeature(element, 'zoneSupport', features, ['zone', 'pressure relief', 'lumbar', 'ergonomic']);
      const edgeSupport = this.hasFeature(element, 'edgeSupport', features, ['edge support', 'reinforced edge']);
      const organic = this.hasFeature(element, 'organic', features, ['organic', 'hypoallergenic', 'natural', 'non-toxic']);
      const pressurePriority = answers.position === 'side' || answers.pain.includes('shoulder') || answers.pain.includes('hip');

      if (pressurePriority) {
        score += pressureRelief * 1.2;
        if (pressureRelief >= 7) reasons.push('Its pressure-relief score supports your shoulders and hips');
      } else {
        score += Math.max(2, 10 - (Math.abs(pressureRelief - 6) * 2));
      }

      if (answers.temperature === 'hot') {
        score += cooling;
        if (cooling >= 7) reasons.push('Its cooling score supports a warmer sleeper');
      } else {
        score += Math.max(3, 8 - Math.abs(cooling - 5));
      }

      if (hasPain && zoneSupport) {
        reasons.push('Zoned or pressure-relieving support aligns with your comfort concerns');
      }
      if (answers.weight === 'heavy' && edgeSupport) {
        reasons.push('Reinforced edges add stability and usable sleep surface');
      }
      if (answers.motion === 'sensitive') {
        score += motionIsolation;
        if (motionIsolation >= 7) reasons.push('Its motion-isolation score suits a light sleeper');
      } else {
        score += Math.max(3, 8 - Math.abs(motionIsolation - 5));
      }
      if (answers.frequency === 'daily' && organic) {
        reasons.push('Natural or hypoallergenic materials add a premium comfort feature');
      }
      if (bestFor.includes(answers.position)) {
        score += 10;
        reasons.push('The model’s Best for profile includes your sleep position');
      } else {
        score += 4;
      }
      if (reasons.length === 0) reasons.push('Closest available overall profile in the selected collection');

      const tieBreakScore = (hasPain && zoneSupport ? 1 : 0) +
        (answers.weight === 'heavy' && edgeSupport ? 1 : 0) +
        (answers.frequency === 'daily' && organic ? 1 : 0);

      return {
        element,
        rawScore: score + tieBreakScore,
        displayScore: Math.max(1, Math.min(100, Math.round(score))),
        priceCents,
        queenPriceCents,
        reasons
      };
    }

    parseMetric(value, fallback = 5) {
      const metric = Number.parseFloat(String(value || ''));
      return Number.isFinite(metric) ? Math.max(1, Math.min(10, metric)) : fallback;
    }

    budgetRange(value) {
      const ranges = {
        'under-1000': { min: 0, max: 99999 },
        '1000-1999': { min: 100000, max: 199999 },
        '2000-3499': { min: 200000, max: 349999 },
        '3500-plus': { min: 350000, max: Number.POSITIVE_INFINITY }
      };
      return ranges[value] || null;
    }

    budgetLabel(value) {
      const labels = {
        'under-1000': 'Under $1,000',
        '1000-1999': '$1,000–$1,999',
        '2000-3499': '$2,000–$3,499',
        '3500-plus': '$3,500+',
        flexible: 'Flexible'
      };
      return labels[value] || labels.flexible;
    }

    budgetAnalyticsEstimate(value) {
      const estimates = {
        'under-1000': 750,
        '1000-1999': 1500,
        '2000-3499': 2750,
        '3500-plus': 4000
      };
      return estimates[value];
    }

    priceIsWithinBudget(priceCents, budget) {
      return Number.isFinite(priceCents) && priceCents >= budget.min && priceCents <= budget.max;
    }

    hasFeature(element, dataKey, featureText, keywords) {
      return element.dataset[dataKey] === 'true' || keywords.some((keyword) => featureText.includes(keyword));
    }

    parseFirmness(value = '') {
      const normalized = String(value).trim().toLowerCase().replace(/[_-]+/g, ' ');
      const numeric = Number.parseFloat(normalized);
      if (Number.isFinite(numeric)) return Math.max(1, Math.min(10, numeric));

      const map = [
        ['ultra plush', 2],
        ['extra soft', 2],
        ['medium plush', 4.5],
        ['medium soft', 4.5],
        ['medium firm', 6.5],
        ['extra firm', 9],
        ['ultra firm', 9.5],
        ['plush', 3.5],
        ['soft', 3],
        ['medium', 5],
        ['firm', 8]
      ];
      const match = map.find(([label]) => normalized.includes(label));
      return match ? match[1] : Number.NaN;
    }

    normalizeType(value = '') {
      const normalized = String(value).toLowerCase();
      if (normalized.includes('hybrid')) return 'hybrid';
      if (normalized.includes('memory foam') || normalized.includes('all foam') || normalized.includes('latex') || normalized.includes('foam')) return 'foam';
      if (
        normalized.includes('innerspring') ||
        normalized.includes('inner spring') ||
        normalized.includes('traditional coil') ||
        normalized.includes('pocket coil') ||
        normalized.includes('pocketed coil') ||
        normalized.includes('coil')
      ) return 'innerspring';
      return 'unknown';
    }

    displayType(type) {
      const names = { foam: 'Memory Foam', hybrid: 'Hybrid', innerspring: 'Innerspring', unknown: 'Mattress' };
      return names[type] || names.unknown;
    }

    restart() {
      const nextAttemptNumber = this.attemptNumber + 1;
      this.trackEvent('sleep_quiz_retaken', {
        previous_firmness_label: this.latestPrescription?.classification || '',
        previous_construction: this.latestPrescription?.type || '',
        next_attempt_number: nextAttemptNumber
      });
      this.attemptNumber = nextAttemptNumber;
      this.quizStarted = false;
      this.answeredSteps.clear();
      this.completionTrackedForAttempt = false;
      this.form.reset();
      this.results.hidden = true;
      this.form.hidden = false;
      this.showStep(0);
      this.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    getMatchDetails(match) {
      const element = match && match.element;
      const titleLink = element ? element.querySelector('h3 a') : null;
      const vendor = element ? element.querySelector('.sleep-quiz__vendor') : null;
      const price = element ? element.querySelector('.sleep-quiz__price') : null;
      return {
        title: titleLink ? titleLink.textContent.trim() : 'Mattress match',
        vendor: vendor ? vendor.textContent.trim() : 'Canadian Mattress',
        price: price ? price.textContent.trim().replace(/\s+/g, ' ') : '',
        score: match ? match.displayScore : 0
      };
    }

    pdfAscii(value = '') {
      return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u2013\u2014]/g, '-')
        .replace(/[^\x20-\x7E]/g, '');
    }

    pdfEscape(value = '') {
      return this.pdfAscii(value).replace(/([\\()])/g, '\\$1');
    }

    pdfWrap(value, maxCharacters) {
      const words = this.pdfAscii(value).split(/\s+/).filter(Boolean);
      const lines = [];
      let line = '';
      words.forEach((word) => {
        const next = line ? `${line} ${word}` : word;
        if (next.length > maxCharacters && line) {
          lines.push(line);
          line = word;
        } else {
          line = next;
        }
      });
      if (line) lines.push(line);
      return lines;
    }

    createSleepMatchPdf(prescription, matches, incentive, incentiveDetails, budgetLabel) {
      const commands = [];
      const addRect = (x, y, width, height, color) => commands.push(`${color} rg ${x} ${y} ${width} ${height} re f`);
      const addLine = (x1, y1, x2, y2, color = '0.84 0.84 0.84', width = 1) => commands.push(`${color} RG ${width} w ${x1} ${y1} m ${x2} ${y2} l S`);
      const addText = (value, x, y, size, font = 'F1', color = '0.12 0.12 0.12') => {
        commands.push(`${color} rg BT /${font} ${size} Tf 1 0 0 1 ${x} ${y} Tm (${this.pdfEscape(value)}) Tj ET`);
      };
      const addWrappedText = (value, x, y, size, maxCharacters, lineHeight, font = 'F1', color = '0.12 0.12 0.12', maxLines = 2) => {
        this.pdfWrap(value, maxCharacters).slice(0, maxLines).forEach((line, index) => addText(line, x, y - (index * lineHeight), size, font, color));
      };

      addRect(0, 0, 612, 792, '0.965 0.957 0.941');
      addRect(0, 650, 612, 142, '0.075 0.075 0.078');
      addRect(0, 786, 612, 6, '0.86 0.04 0.07');
      addText('CANADIAN MATTRESS', 54, 752, 10, 'F2', '0.94 0.12 0.14');
      addText('YOUR SLEEP MATCH', 54, 713, 27, 'F2', '1 1 1');
      addText('A practical starting point for comparing mattresses online or in store.', 54, 684, 11, 'F1', '0.78 0.78 0.78');
      addText(`QUEEN BUDGET: ${budgetLabel}`, 54, 663, 9, 'F2', '0.94 0.12 0.14');

      addText('TARGET FIRMNESS', 54, 614, 9, 'F2', '0.42 0.42 0.42');
      addText(`${prescription.firmness.toFixed(1)}/10`, 54, 576, 28, 'F2');
      addText(prescription.classification, 147, 580, 12, 'F2', '0.86 0.04 0.07');
      addText('RECOMMENDED CONSTRUCTION', 382, 614, 9, 'F2', '0.42 0.42 0.42');
      addText(this.displayType(prescription.type), 382, 580, 18, 'F2');
      addRect(54, 545, 504, 9, '0.83 0.83 0.83');
      addRect(54, 545, Math.max(10, 504 * (prescription.firmness / 10)), 9, '0.86 0.04 0.07');
      addText('SOFT', 54, 526, 8, 'F2', '0.42 0.42 0.42');
      addText('MEDIUM', 285, 526, 8, 'F2', '0.42 0.42 0.42');
      addText('FIRM', 531, 526, 8, 'F2', '0.42 0.42 0.42');

      addText('YOUR TWO MATTRESS MATCHES', 54, 506, 11, 'F2', '0.86 0.04 0.07');
      const details = matches.slice(0, 2).map((match) => this.getMatchDetails(match));
      [388, 280].forEach((y, index) => {
        const match = details[index];
        addRect(54, y, 504, 88, '1 1 1');
        addRect(54, y, 8, 88, index === 0 ? '0.86 0.04 0.07' : '0.14 0.14 0.15');
        addText(`0${index + 1}`, 76, y + 57, 15, 'F2', index === 0 ? '0.86 0.04 0.07' : '0.14 0.14 0.15');
        if (match) {
          addText(match.vendor.toUpperCase(), 118, y + 62, 8, 'F2', '0.45 0.45 0.45');
          addWrappedText(match.title, 118, y + 41, 13, 39, 15, 'F2', '0.12 0.12 0.12', 2);
          if (match.price) addText(match.price, 118, y + 8, 9, 'F1', '0.32 0.32 0.32');
          addText(`${match.score}% MATCH`, 461, y + 54, 10, 'F2', '0.86 0.04 0.07');
        } else {
          addText('Complete product details in store', 118, y + 38, 12, 'F2');
        }
      });

      addRect(54, 158, 504, 98, '0.075 0.075 0.078');
      addText('IN-STORE PURCHASE OFFER', 74, 233, 9, 'F2', '0.94 0.12 0.14');
      addWrappedText(incentive || 'Complimentary Comfy Essential Sheet Set with an additional in-store purchase', 74, 210, 13, 59, 15, 'F2', '1 1 1', 2);
      addWrappedText(incentiveDetails || 'In-store redemption only. Present this completed Sleep Match, printed or on your phone, when making your purchase.', 74, 174, 8, 88, 10, 'F1', '0.78 0.78 0.78', 2);
      addLine(54, 148, 558, 148);
      addText('TRY YOUR MATCHES IN PERSON', 54, 124, 9, 'F2', '0.42 0.42 0.42');
      addText('Calgary: 6624 Centre St S  |  403-455-4466', 54, 102, 10);
      addText('Edmonton: 5111 99th Street  |  780-455-4466', 54, 84, 10);
      addText('gotbeds.ca  |  Bring this summary to a sleep consultant for guided comparison.', 54, 60, 9, 'F1', '0.42 0.42 0.42');
      addText('General guidance only. Comfort is personal and this is not medical advice.', 54, 42, 8, 'F1', '0.52 0.52 0.52');

      const content = commands.join('\n');
      const encoder = new TextEncoder();
      const objects = [
        '<< /Type /Catalog /Pages 2 0 R >>',
        '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
        '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R /F2 5 0 R >> >> /Contents 6 0 R >>',
        '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
        '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>',
        `<< /Length ${encoder.encode(content).length} >>\nstream\n${content}\nendstream`
      ];
      let pdf = '%PDF-1.4\n%CM-SLEEP-MATCH\n';
      const offsets = [0];
      objects.forEach((object, index) => {
        offsets.push(encoder.encode(pdf).length);
        pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
      });
      const xrefOffset = encoder.encode(pdf).length;
      pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
      offsets.slice(1).forEach((offset) => {
        pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
      });
      pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
      return new Blob([encoder.encode(pdf)], { type: 'application/pdf' });
    }

    downloadSleepMatch() {
      if (!this.latestPrescription) {
        alert('Please complete the quiz first to create your sleep match.');
        return;
      }

      const incentiveEl = this.querySelector('.sleep-quiz__reward h4');
      const incentive = incentiveEl ? incentiveEl.textContent.trim() : '';
      const incentiveDetailsEl = this.querySelector('.sleep-quiz__reward p');
      const incentiveDetails = incentiveDetailsEl ? incentiveDetailsEl.textContent.trim() : '';
      try {
        const budgetLabel = this.budgetLabel(this.latestAnswers?.budget);
        const blob = this.createSleepMatchPdf(this.latestPrescription, this.latestMatches || [], incentive, incentiveDetails, budgetLabel);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Canadian-Mattress-Sleep-Match.pdf';
        document.body.appendChild(link);
        link.click();
        this.trackEvent('sleep_quiz_pdf_downloaded', this.resultAnalyticsParameters());
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } catch (error) {
        console.error('Sleep match PDF download failed:', error);
        alert('The PDF could not be downloaded. Please open the live quiz page outside the theme editor and try again.');
      }
    }

    shareSleepMatch() {
      if (!this.latestPrescription) return;

      const prescription = this.latestPrescription;
      const matches = this.latestMatches || [];

      const incentiveEl = this.querySelector('.sleep-quiz__reward h4');
      const incentive = incentiveEl ? incentiveEl.textContent.trim() : '';
      const incentiveDetailsEl = this.querySelector('.sleep-quiz__reward p');
      const incentiveDetails = incentiveDetailsEl ? incentiveDetailsEl.textContent.trim() : '';

      let text = `My Canadian Mattress Sleep Match:\n` +
                 `Target Firmness: ${prescription.firmness.toFixed(1)}/10 (${prescription.classification})\n` +
                 `Type: ${this.displayType(prescription.type)}\n` +
                 `Queen budget: ${this.budgetLabel(this.latestAnswers?.budget)}\n`;

      if (matches.length > 0) {
        text += '\nMy matches:\n';
        matches.forEach((match) => {
          const title = match.element.querySelector('h3').textContent.trim();
          text += `- ${title} (${match.displayScore}% Match)\n`;
        });
      }

      if (incentive) {
        text += `\nIn-store purchase offer: ${incentive}\n`;
        if (incentiveDetails) text += `${incentiveDetails}\n`;
      }

      text += `\nView more at: ${window.location.href}`;

      if (navigator.share) {
        navigator.share({
          title: 'My Canadian Mattress Sleep Match',
          text: text,
          url: window.location.href
        }).then(() => {
          this.trackEvent('sleep_quiz_results_shared', {
            share_method: 'web_share'
          });
        }).catch(() => {});
      } else {
        navigator.clipboard.writeText(text).then(() => {
          this.trackEvent('sleep_quiz_results_shared', {
            share_method: 'clipboard'
          });
          if (this.shareToast) {
            this.shareToast.hidden = false;
            setTimeout(() => {
              this.shareToast.hidden = true;
            }, 3000);
          }
        }).catch(() => {
          alert('Could not copy results to clipboard.');
        });
      }
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    updateSelectionClasses() {
      const options = Array.from(this.querySelectorAll('.sleep-quiz__option'));
      options.forEach((option) => {
        const input = option.querySelector('input');
        if (input && input.checked) {
          option.classList.add('is-selected');
        } else {
          option.classList.remove('is-selected');
        }
      });
    }
  });
}
